/**
 * ReceiveModal — shows account QR code + copy button
 * Opens when user taps "Receive" in WalletScreen
 */

import { useState } from 'react'
import { useWalletStore } from '../store/wallet.store'
import { useTranslation } from '../hooks/useTranslation'

interface ReceiveModalProps {
  onClose: () => void
}

export default function ReceiveModal({ onClose }: ReceiveModalProps) {
  const { activeAccount } = useWalletStore()
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  const accountName = activeAccount?.name ?? ''

  // Free, no-auth QR code service — white bg with dark modules so it's scannable
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(accountName)}&format=png&margin=4`

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(accountName)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      // Fallback for environments without clipboard API
      const el = document.createElement('textarea')
      el.value = accountName
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/75 flex items-end sm:items-center justify-center"
      role="dialog"
      aria-modal="true"
    >
      {/* backdrop tap-to-close */}
      <button
        type="button"
        className="absolute inset-0 cursor-default"
        aria-label={t('common.close')}
        onClick={onClose}
      />

      <div className="relative w-full max-w-sm sm:rounded-2xl rounded-t-2xl bg-surface-1 border border-surface-3 shadow-2xl p-6 flex flex-col items-center gap-4 mt-auto sm:mt-0 animate-fade-in">
        {/* close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-white text-lg p-1"
          aria-label={t('common.close')}
        >
          ✕
        </button>

        <h3 className="text-white font-semibold text-lg">{t('wallet.receive')}</h3>

        {/* QR code */}
        <div className="bg-white rounded-2xl p-3 shadow-lg">
          <img
            src={qrUrl}
            alt={`QR code for ${accountName}`}
            width={220}
            height={220}
            className="rounded-lg block"
          />
        </div>

        {/* Account name chip */}
        <div className="w-full bg-surface-2 rounded-xl px-4 py-3 text-center space-y-1">
          <p className="text-xs text-gray-400 uppercase tracking-wide">{t('wallet.copyAddress')}</p>
          <p className="text-white font-mono font-bold text-xl tracking-wider">{accountName}</p>
          <p className="text-xs text-gray-500">EOS / Vaulta</p>
        </div>

        {/* Action buttons */}
        <div className="flex gap-3 w-full">
          <button
            onClick={handleCopy}
            className={`flex-1 py-3 rounded-xl text-sm font-semibold transition-all ${
              copied
                ? 'bg-success/20 text-success border border-success/40'
                : 'btn-primary'
            }`}
          >
            {copied ? `✓ ${t('common.copied')}` : t('common.copy')}
          </button>
          <button onClick={onClose} className="flex-1 btn-secondary py-3 text-sm">
            {t('common.close')}
          </button>
        </div>
      </div>
    </div>
  )
}
